import { BreadCrumbs, Chip, Container, Tabs, TypoGraph } from 'components'
import { CARRIER_DETAILS_VIEW, carrierDetailsTabs, carrierStatus, fleetDetailsTabsForAgents } from 'constants/status'
import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Typography, Box, ToggleButton, ToggleButtonGroup } from '@mui/material'
import colors from 'theme/colors'
import InsuranceCarriers from '../../insuranceCarriers'
import HeartbeatDashboard from '../../heartbeat'
import HeartbeatTrendsDashboard from '../../heartbeatTrends'
import MileageReportingDashboard from 'pages/reporting/partial/mileageReporting'
import LocationReportingDashboard from 'pages/reporting/partial/locationReporting'
import RiskDecileDashboard from 'pages/reporting/partial/riskDecile'
import FleetMilesByZipDashboard from '../../fleetMilesByZip'
import FleetDetailsForAgents from '../../fleetDetailsForAgent'
import { FleetPercentiles } from 'pages/connectedAccounts/partial'
import { isAgentLogin, isInsuranceLogin } from 'tools/auth'
import RadiusOfOperationDashboard from '../../radiusOfOperation'
import SafetyEventsDashboard from '../../safetyEvents'
import DriversDashboard from '../../drivers'
import { formatDate, isShortcodeExcludedForDriversDashboardAccess, isShortcodeEnabledForUnderwritersScoreDashboard } from 'tools/helper'
import CarrierSummaryReport from '../../summaryReport'
import UnderwritersScore from '../../underwritersScore'
import UbiSummaryDashboard from '../../ubiSummary'
import { REPORT_KEYS, getReportFlagKey } from 'constants/reporting'
import { useFeatureFlag } from 'contexts/ReportingContext'
import strings from 'strings'
import { getActiveUnitsWithCameras } from 'api/carriers'
import LoadingDots from 'components/loadingDots'
import { reportException } from 'tools/logs'
import { useLocation } from 'react-router'
import { useCarrierCodeBootstrap } from 'hooks/useCarrierCodeBootstrap'

const t = strings.TABLE_DEFAULTS
const TABS = strings.CARRIERS.TABS

interface ICarrierDetailDefaultProps {
    changeCarrierDetailsView?: () => void
}

const CarrierDetailsDefault: React.FC<ICarrierDetailDefaultProps> = ({ changeCarrierDetailsView }) => {
    const location = useLocation()
    const path = location.pathname
    const carrierData = useCarrierCodeBootstrap()
    const [activeUnitsWithCamerasLoading, setActiveUnitsWithCamerasLoading] =
        useState<boolean>(true)
    const [activeUnitsWithCameras, setActiveUnitsWithCameras] = useState<number>()
    const [carrierDetailsTab, setCarrierDetailsTab] = useState(0)
    const carrierInfoRef = useRef<HTMLDivElement>(null)

    const ubiEnabled = useFeatureFlag(getReportFlagKey(REPORT_KEYS.UBI_SUMMARY))
    const isInsurer = isInsuranceLogin()
    const insuranceTabs = useMemo(
        () => [
            ...(ubiEnabled && isInsurer ? [{ label: TABS.UBI_SUMMARY }] : []),
            ...carrierDetailsTabs,
        ],
        [ubiEnabled, isInsurer]
    )
    const displayedTabs = isAgentLogin() ? fleetDetailsTabsForAgents : insuranceTabs

    const enhancedDisplayedTabs = useMemo(() => {
        const tabTestIds: Record<string, string> = {
            [TABS.NBI_TELEMATICS_SCORE]: 'carrier-details-default-tab-telematics',
        }
        return displayedTabs.map((tab) => ({
            ...tab,
            dataTestId: tabTestIds[tab.label],
        }))
    }, [displayedTabs])

    useEffect(() => {
      if (!carrierData?.carrierId) {
        setActiveUnitsWithCamerasLoading(false)
        return
      }
      setActiveUnitsWithCamerasLoading(true)
      getActiveUnitsWithCameras(carrierData?.carrierId)
        .then((data: any) => {
          setActiveUnitsWithCameras(data?.totalWithCameras || 0)
        })
        .catch(reportException)
        .finally(() => setActiveUnitsWithCamerasLoading(false))
    }, [carrierData?.carrierId])

    const ref = useRef<HTMLDivElement>(null)

    const today = new Date().toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    })

    const InsuranceTabPanel = useCallback(
        (props: {
            print: boolean
            carrierDetailsTab: number
            carrierInfoRef: React.RefObject<HTMLDivElement>
            carrierInfoLoading: boolean
        }) => {
            const { print, carrierDetailsTab, carrierInfoRef, carrierInfoLoading } = props
            const currentTabLabel = insuranceTabs[carrierDetailsTab]?.label

            switch (currentTabLabel) {
                case TABS.UBI_SUMMARY:
                    return <UbiSummaryDashboard />
                case TABS.NBI_TELEMATICS_SCORE:
                    return <UnderwritersScore print={print} />
                case TABS.SUMMARY_REPORT:
                    return (
                        <CarrierSummaryReport
                            print={print}
                            carrierInfoRef={carrierInfoRef}
                            carrierInfoLoading={carrierInfoLoading}
                        />
                    )
                case TABS.DETAILS:
                    return <InsuranceCarriers print={print} />
                case TABS.HEARTBEAT:
                    return <HeartbeatDashboard />
                case TABS.HEARTBEAT_TRENDS:
                    return <HeartbeatTrendsDashboard print={print} useCarrierFilters={true} />
                case TABS.MILEAGE_REPORTING:
                    return <MileageReportingDashboard print={print} useCarrierFilters={true} />
                case TABS.LOCATION_REPORTING:
                    return <LocationReportingDashboard print={print} useCarrierFilters={true} />
                case TABS.MILES_BY_ZIP:
                    return <FleetMilesByZipDashboard print={print} useCarrierFilters={true} />
                case TABS.RADIUS_OF_OPERATION:
                    return <RadiusOfOperationDashboard print={print} useCarrierFilters={true} />
                case TABS.SAFETY_EVENTS:
                    return <SafetyEventsDashboard print={print} useCarrierFilters={true} carrierInfoRef={carrierInfoRef} />
                case TABS.DRIVERS:
                    return <DriversDashboard />
                case TABS.RISK_DECILE:
                    return <RiskDecileDashboard useCarrierFilters={true} />
                default:
                    return <></>
            }
        },
        [insuranceTabs]
    )

    const AgentTabPanel = useCallback((props: { print: boolean }) => {
        const { print } = props
        switch (carrierDetailsTab) {
            case 0:
                return <FleetDetailsForAgents print={print} />
            case 1:
                return <HeartbeatDashboard />
            case 2:
                return <FleetPercentiles print={print} />
            case 3:
                return <FleetMilesByZipDashboard print={print} useCarrierFilters={true} />
            case 4:
                return <SafetyEventsDashboard print={print} useCarrierFilters={true} carrierInfoRef={carrierInfoRef} />
            default:
                return <></>
        }
    }, [carrierDetailsTab])

    const getPageMargins = () => {
        return `@page { margin: 30px!important; }`
    }

    return (
        <Container data-testid="carrier-details-default-page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <BreadCrumbs path={path} />
                {changeCarrierDetailsView && (
                    <ToggleButtonGroup
                        value={CARRIER_DETAILS_VIEW.INSURANCE}
                        exclusive
                        onChange={(_, newView) => newView === CARRIER_DETAILS_VIEW.FLEET && changeCarrierDetailsView()}
                        sx={{
                            flexShrink: 0,
                            backgroundColor: colors.platinum,
                            borderRadius: '6px',
                            padding: '1px',
                            border: `1px solid ${colors.silver}`,
                            '& .MuiToggleButtonGroup-grouped': {
                                border: 0,
                                borderRadius: '4px',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.8125rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                '&.Mui-selected': {
                                    backgroundColor: colors.white,
                                    color: colors.primary,
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                                    '&:hover': {
                                        backgroundColor: colors.white,
                                        color: colors.primary,
                                    },
                                },
                                '&:not(.Mui-selected)': {
                                    color: colors.grey,
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                        color: colors.primary,
                                    },
                                },
                            },
                        }}
                    >
                        <ToggleButton value={CARRIER_DETAILS_VIEW.FLEET} data-testid="carrier-details-default-toggle-insurance" aria-label={strings.CARRIERS.CHANGE_TO_FLEET_VIEW}>
                            {strings.CARRIERS.CHANGE_TO_FLEET_VIEW}
                        </ToggleButton>
                        <ToggleButton
                            value={CARRIER_DETAILS_VIEW.INSURANCE}
                            data-testid="carrier-details-default-toggle"
                            aria-label={strings.CARRIERS.INSURANCE_VIEW}
                        >
                            {strings.CARRIERS.INSURANCE_VIEW}
                        </ToggleButton>
                    </ToggleButtonGroup>
                )}

            </Box>
            <Box ref={carrierInfoRef} sx={{ mt: 3, mb: 2, ml: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {localStorage.getItem('carrierName')}
                    </Typography>
                    <Chip
                        label={`Carrier Status: ` + (carrierStatus[localStorage.getItem('carrierStatus') as keyof typeof carrierStatus]?.label ?? t.DASH_VALUE)}
                        variant="filled"
                        sx={{
                            backgroundColor: carrierStatus[localStorage.getItem('carrierStatus') as keyof typeof carrierStatus]?.background ?? '#E3E6E8',
                            color: carrierStatus[localStorage.getItem('carrierStatus') as keyof typeof carrierStatus]?.color ?? '#707A8B',
                            fontSize: '1rem',
                            padding: '8px 16px',
                            height: 'auto',
                            '& .MuiChip-label': {
                                padding: '4px 8px',
                            }
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        TruckerCloud Onboarding Date: {localStorage.getItem('onboardingDate') ? formatDate(localStorage.getItem('onboardingDate') || '', false) : '-'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Effective Date: {today}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        TSP: {localStorage.getItem('eldProviderRisk')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        DOT:{carrierData?.carrierCodeType === 'DOT' && carrierData?.carrierCodeValue
                            ? ` US DOT ${carrierData?.carrierCodeValue}`
                            : ' -'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Policy ID:{carrierData?.carrierPolicyId && carrierData?.carrierPolicyId !== '-'
                            ? ` ${carrierData?.carrierPolicyId}`
                            : ' -'}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Units with Cameras:{' '}
                        {activeUnitsWithCamerasLoading ? (
                            <LoadingDots color="#666666" />
                        ) : (
                            activeUnitsWithCameras
                        )}
                    </Typography>
                </Box>
            </Box>
            <Tabs
                tabData={enhancedDisplayedTabs}
                value={carrierDetailsTab}
                id={displayedTabs[carrierDetailsTab]?.label}
                handleChange={(_e, val) => setCarrierDetailsTab(val)}
                sxContainer={{
                    marginLeft: '1rem',
                    width: '100%',
                    borderBottom: '1px solid #e0e0e0',
                }}
                sxTabs={{
                    '& .MuiTabs-indicator': {
                        backgroundColor: '#0072BC',
                        height: '3px',
                    },
                }}
            />
            <Container>
                {isAgentLogin() ?
                    <AgentTabPanel print={false} />
                    :
                    <InsuranceTabPanel
                    print={false}
                    carrierDetailsTab={carrierDetailsTab}
                    carrierInfoRef={carrierInfoRef}
                    carrierInfoLoading={activeUnitsWithCamerasLoading}
                />
                }
            </Container>
            <style>{getPageMargins()}</style>
        </Container>
    )
}

export default CarrierDetailsDefault